/**
 * OpDataBatchCard — interactive sign-off card for `propose_operational_data_batch` (PLT-5088).
 *
 * The chat-only tool `propose_operational_data_batch` validates a structured proposal (one or more
 * operational-data splits the agent extracted from a user's uploaded file) and echoes it back as the
 * tool result. This card renders that payload as one block per {dimension, metric} dataset:
 *
 *   - a card subtitle naming the report year and the year the data is saved for — always the current
 *     year, like the manual form, so the data shows in the form and the DMA uses it;
 *   - a header naming the metric and breakdown dimension;
 *   - an editable list of per-key percentage rows. For the COUNTRY dimension the user may also add,
 *     delete, and change rows — the key is a picker constrained to Upright's standard country list
 *     (PLT-5088). The INDUSTRY dimension is a fixed high-impact-industry list, so its keys are not
 *     editable (only the percentages are);
 *   - validation chips computed client-side with the SAME canonical rules the backend enforces at
 *     commit (`validatePercentageData`) — negative / >100% individual / >100% total block apply,
 *     <50% total is a non-blocking auditability warning (suppressed for the suppliers-by-industry
 *     subset, where a partial total is expected);
 *   - a per-dataset Confirm (with an inline are-you-sure step) plus a "Save all" affordance.
 *
 * Confirming a dataset does NOT route back through the LLM — it POSTs directly to
 * `POST /api/company/{companyVariantId}/operational-data/batch` (idempotent on `batchId`), so the
 * commit is deterministic and audit-clean. Applying a dataset is a FULL REPLACE of that
 * {dimension, metric} scope (PLT-5088 Q3). After a successful apply the dataset collapses into
 * an immutable receipt. The agent never writes.
 *
 * Sustainability indicators found in the same file render as one more block
 * (`<SustainabilityIndicatorsBlock>`), so one upload gives one card; "Save all" covers them too.
 *
 * Weights are edited and validated here as PERCENTAGES (0–100), matching the manual operational-data
 * form; each is converted to the `[0, 1]` fraction the endpoint stores at apply time.
 *
 * Why a single self-contained file: the card is one tool's UI; nothing else composes its sub-pieces.
 * `<OpDataBatchCard>` is the sole export, mirroring `tailoring-batch-card.tsx`.
 *
 * Restricted-company handling: applying is gated server-side (and pre-checked in the tool via
 * `checkOperationalDataApplyAccess`); the card is a presentation + client-state layer and re-fetches
 * no company data.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ApplyOperationalDataBatchResult,
  MAX_OPERATIONAL_DATA_BATCH_DATASETS,
} from '@upright/common/model/input/companies/operational-data/api-types';
import {
  CompanyOperationsDimension,
  CompanyOperationsMetricType,
} from '@upright/common/model/input/companies/operational-data/types';
import {
  type OperationalDataValidationResult,
  validatePercentageData,
} from '@upright/common/model/input/companies/operational-data/validation';
import { DMA_COUNTRIES } from '@upright/common/model/input/companies/operational-data/dma-countries';
import {
  isValidOperationalDataCountry,
  resolveOperationalDataCountry,
} from '@upright/common/model/input/companies/operational-data/resolve-operational-data-country';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { HAS_UNINTEGRATED_CHANGES_QUERY_KEY } from '@upright/ui/common/generic/components/operational-data-input/query-keys';
import { ChatMarkdown } from './upright-chat-markdown';
import { Badge } from './components';
import { savedForYearLabel } from './chat-upload-year';
import {
  type ProposedIndicator,
  SustainabilityIndicatorsBlock,
  useSustainabilityIndicatorsApply,
} from './sustainability-indicators-batch-card';

// ---------------------------------------------------------------------------
// Types — the shape the chat-only tool echoes back. Kept inline rather than
// re-imported from the backend module so the UI bundle does not pull in zod.
// Structurally matches `ProposeOperationalDataBatchInput` in
// `mcp/tools/propose-operational-data-batch.ts`.
// ---------------------------------------------------------------------------

interface ProposedRow {
  key: string;
  weightPercent: number;
}

interface ProposedDataset {
  batchId: string;
  dimension: CompanyOperationsDimension;
  operationsMetricType: CompanyOperationsMetricType;
  reportYear?: number;
  rows: ProposedRow[];
  note?: string;
}

export interface OpDataBatchCardProps {
  companyId: string;
  companyName?: string;
  /**
   * The company variant the chat is bound to — the value the batch-apply endpoint takes in its path
   * (`/company/{companyVariantId}/operational-data/batch`); the backend resolves it to the company.
   * Pulled from the company-page binding by the tool UI wrapper — when no variant is bound the card
   * disables saving and shows an explanatory hint.
   */
  variantId: string | null;
  proposalId: string;
  summaryMarkdown?: string;
  datasets: ProposedDataset[];
  /** Indicators found in the same file(s), shown as one more block so one upload gives one card. */
  sustainabilityIndicators?: { reportYear?: number; indicators: ProposedIndicator[] };
}

const NO_INDICATORS: ProposedIndicator[] = [];

const DIMENSION_LABEL: Record<CompanyOperationsDimension, string> = {
  [CompanyOperationsDimension.COUNTRY]: 'Country',
  [CompanyOperationsDimension.INDUSTRY]: 'Industry',
};

const METRIC_LABEL: Record<CompanyOperationsMetricType, string> = {
  [CompanyOperationsMetricType.REVENUE]: 'Revenue',
  [CompanyOperationsMetricType.SUPPLIERS]: 'Suppliers',
  [CompanyOperationsMetricType.EMPLOYEES]: 'Employees',
  [CompanyOperationsMetricType.PRODUCTION_SITES]: 'Production sites',
};

function datasetScopeLabel(dataset: ProposedDataset): string {
  return `${METRIC_LABEL[dataset.operationsMetricType]} by ${DIMENSION_LABEL[dataset.dimension].toLowerCase()}`;
}

// ---------------------------------------------------------------------------
// Per-dataset state machine — editable → confirming → applying → receipt.
// "Confirming" sits between the editable list and the write: clicking Save
// surfaces a Confirm / Cancel prompt because applying replaces the whole
// scope. "Failed" is a transient overlay on the editable phase — the dataset
// stays editable so the user can fix and retry.
// ---------------------------------------------------------------------------

type DatasetState =
  | { phase: 'idle' }
  | { phase: 'confirming' }
  | { phase: 'applying' }
  | { phase: 'applied'; result: ApplyOperationalDataBatchResult }
  | { phase: 'failed'; message: string };

// An editable working row. `percent` is the raw input string (so the user can clear / type freely);
// empty string is treated as null (i.e. 0) by the canonical validator. `key` is editable only for
// the COUNTRY dimension (INDUSTRY keys are fixed to the high-impact-industry enum).
interface WorkingRow {
  key: string;
  percent: string;
}

// Lazily-initialized working copy of each dataset's rows, keyed by batchId. Absent → the dataset has
// not been touched, so we derive the working rows from the agent's proposal on the fly.
type WorkingRows = Record<string, WorkingRow[]>;

// Country keys start out in the list's spelling ("Czech Republic" → "Czechia") so the picker shows
// them selected. The tool already does this; repeating it here covers proposals saved before it did.
function initialRows(dataset: ProposedDataset): WorkingRow[] {
  const isCountry = dataset.dimension === CompanyOperationsDimension.COUNTRY;
  return dataset.rows.map(row => ({
    key: isCountry ? (resolveOperationalDataCountry(row.key) ?? row.key) : row.key,
    percent: String(row.weightPercent),
  }));
}

function workingRowsFor(dataset: ProposedDataset, working: WorkingRows): WorkingRow[] {
  return working[dataset.batchId] ?? initialRows(dataset);
}

interface EffectiveRow {
  key: string;
  percentage: number | null;
}

function toEffective(rows: WorkingRow[]): EffectiveRow[] {
  return rows.map(row => {
    const trimmed = row.percent.trim();
    if (trimmed === '') return { key: row.key, percentage: null };
    const parsed = Number(trimmed);
    return { key: row.key, percentage: Number.isNaN(parsed) ? null : parsed };
  });
}

// Validate a dataset's working rows with the canonical percentage rules (scoped so the
// suppliers-by-industry subset is exempt from the <50% warning), plus country-key checks the
// canonical validator does not cover: COUNTRY rows must each name a country from the standard list,
// with no blanks and no duplicates. These are surfaced as blocking errors so the card never POSTs a
// row the server would reject.
function validateDataset(dataset: ProposedDataset, rows: WorkingRow[]): OperationalDataValidationResult {
  const base = validatePercentageData(toEffective(rows), {
    operationsMetricType: dataset.operationsMetricType,
    dimension: dataset.dimension,
  });
  if (dataset.dimension !== CompanyOperationsDimension.COUNTRY) return base;

  const errors = [...base.errors];
  const keys = rows.map(row => row.key.trim());

  if (keys.some(key => key === '')) {
    errors.push('Every country row needs a country selected.');
  }
  const invalid = keys.filter(key => key !== '' && !isValidOperationalDataCountry(key));
  if (invalid.length > 0) {
    errors.push(`Not in the country list: ${invalid.join(', ')}.`);
  }
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const key of keys) {
    if (key === '') continue;
    // Compare canonical names, so "USA" and "United States" count as the same country.
    const canonical = resolveOperationalDataCountry(key) ?? key.toLowerCase();
    if (seen.has(canonical)) duplicates.add(key);
    seen.add(canonical);
  }
  if (duplicates.size > 0) {
    errors.push(`Duplicate countries: ${[...duplicates].join(', ')}.`);
  }

  return { ...base, errors, isValid: errors.length === 0, canSave: errors.length === 0 };
}

// ---------------------------------------------------------------------------
// Top-level component.
// ---------------------------------------------------------------------------

export function OpDataBatchCard({
  companyId,
  companyName,
  variantId,
  proposalId: _proposalId,
  summaryMarkdown,
  datasets,
  sustainabilityIndicators,
}: OpDataBatchCardProps) {
  // Per-dataset working rows and apply state, keyed by batchId.
  const [working, setWorking] = useState<WorkingRows>({});
  const [states, setStates] = useState<Record<string, DatasetState>>({});
  const queryClient = useQueryClient();
  // Card-level are-you-sure for "Save all", mirroring the per-dataset confirm step.
  const [confirmingAll, setConfirmingAll] = useState(false);
  // The indicators block (if any). The hook runs unconditionally; with no indicators it is inert.
  const hasIndicators = sustainabilityIndicators !== undefined && sustainabilityIndicators.indicators.length > 0;
  const indicatorsBlock = useSustainabilityIndicatorsApply(
    companyId,
    sustainabilityIndicators?.indicators ?? NO_INDICATORS,
    sustainabilityIndicators?.reportYear,
  );
  const indicatorsPhase = indicatorsBlock.state.phase;
  const indicatorsSavable =
    hasIndicators && indicatorsBlock.canSave && indicatorsPhase !== 'applied' && indicatorsPhase !== 'applying';

  const stateFor = (batchId: string): DatasetState => states[batchId] ?? { phase: 'idle' };
  const setState = (batchId: string, next: DatasetState) => {
    setStates(prev => ({ ...prev, [batchId]: next }));
  };

  // All mutations go through here: read the current working rows (lazily seeded from the proposal),
  // transform, and write back. Keeps the proposal prop immutable.
  const mutateRows = (dataset: ProposedDataset, transform: (rows: WorkingRow[]) => WorkingRow[]) => {
    setWorking(prev => ({ ...prev, [dataset.batchId]: transform(prev[dataset.batchId] ?? initialRows(dataset)) }));
  };

  const editPercent = (dataset: ProposedDataset, idx: number, value: string) => {
    mutateRows(dataset, rows => rows.map((row, i) => (i === idx ? { ...row, percent: value } : row)));
  };
  const editKey = (dataset: ProposedDataset, idx: number, value: string) => {
    mutateRows(dataset, rows => rows.map((row, i) => (i === idx ? { ...row, key: value } : row)));
  };
  const addRow = (dataset: ProposedDataset) => {
    mutateRows(dataset, rows => [...rows, { key: '', percent: '' }]);
  };
  const removeRow = (dataset: ProposedDataset, idx: number) => {
    mutateRows(dataset, rows => rows.filter((_, i) => i !== idx));
  };

  // The single write path, shared by per-dataset Confirm and Save all. Idempotent on batchId server-
  // side, so a double-submit returns the stored result rather than re-applying.
  const applyDataset = async (dataset: ProposedDataset): Promise<void> => {
    if (variantId === null) return;
    const rows = workingRowsFor(dataset, working);
    if (!validateDataset(dataset, rows).canSave) return;
    const effective = toEffective(rows);
    setState(dataset.batchId, { phase: 'applying' });
    try {
      const result = await fetchOrThrow<ApplyOperationalDataBatchResult>(
        `/company/${encodeURIComponent(variantId)}/operational-data/batch`,
        'POST',
        {
          batchId: dataset.batchId,
          // Saved for the current year server-side; the report year is kept for audit only.
          dataset: {
            dimension: dataset.dimension,
            operationsMetricType: dataset.operationsMetricType,
            reportYear: dataset.reportYear,
          },
          // The endpoint stores weights as [0, 1] fractions; a null (cleared) cell commits as 0.
          rows: effective.map(row => ({ key: row.key, weight: (row.percentage ?? 0) / 100 })),
        },
      );
      setState(dataset.batchId, { phase: 'applied', result });
      // The operational-data page may be mounted next to the chat, and neither of its queries polls:
      // refresh the saved split and the "Rerun assessment" eligibility, as `useSaveOperationalData`
      // does for the manual form. Keyed by the company the endpoint resolved the variant to, which
      // is the ID the page queries by.
      void queryClient.invalidateQueries({
        queryKey: ['operational-data', result.companyId, dataset.operationsMetricType, dataset.dimension],
      });
      void queryClient.invalidateQueries({ queryKey: [HAS_UNINTEGRATED_CHANGES_QUERY_KEY] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save this dataset.';
      setState(dataset.batchId, { phase: 'failed', message });
    }
  };

  // Save all: apply every still-editable, currently-valid dataset in sequence. Datasets already
  // applied (or mid-apply) are skipped; invalid ones are left for the user to fix.
  const saveAll = async (): Promise<void> => {
    for (const dataset of datasets) {
      const phase = stateFor(dataset.batchId).phase;
      if (phase === 'applied' || phase === 'applying') continue;
      if (!validateDataset(dataset, workingRowsFor(dataset, working)).canSave) continue;
      // Sequential await keeps the per-dataset state transitions readable and avoids hammering the
      // endpoint with a burst of concurrent writes for the same company.
      await applyDataset(dataset);
    }
    if (indicatorsSavable) {
      await indicatorsBlock.apply();
    }
  };

  const savableDatasetCount = datasets.filter(d => {
    const phase = stateFor(d.batchId).phase;
    if (phase === 'applied' || phase === 'applying') return false;
    return validateDataset(d, workingRowsFor(d, working)).canSave;
  }).length;
  const savableCount = savableDatasetCount + (indicatorsSavable ? 1 : 0);
  // Blocks Save all leaves out because they have errors. Named next to the button and in its
  // confirmation, so a partial save is never silent.
  const blockedLabels = [
    ...datasets
      .filter(d => {
        const phase = stateFor(d.batchId).phase;
        if (phase === 'applied' || phase === 'applying') return false;
        return !validateDataset(d, workingRowsFor(d, working)).canSave;
      })
      .map(datasetScopeLabel),
    ...(hasIndicators && !indicatorsBlock.canSave && indicatorsPhase !== 'applied'
      ? ['Sustainability indicators']
      : []),
  ];
  const blockedHint =
    blockedLabels.length > 0 ? `Not saved until fixed: ${blockedLabels.join(', ')} — see the errors above.` : null;

  const allApplied =
    datasets.every(d => stateFor(d.batchId).phase === 'applied') && (!hasIndicators || indicatorsPhase === 'applied');
  const anyApplied = datasets.some(d => stateFor(d.batchId).phase === 'applied') || indicatorsPhase === 'applied';

  const currentYear = new Date().getFullYear();
  const yearLabel = savedForYearLabel([
    ...datasets.map(d => d.reportYear),
    ...(hasIndicators ? [sustainabilityIndicators.reportYear] : []),
  ]);
  const contents = [
    `${datasets.length} ${datasets.length === 1 ? 'dataset' : 'datasets'}`,
    ...(hasIndicators
      ? [
          `${sustainabilityIndicators.indicators.length} ${
            sustainabilityIndicators.indicators.length === 1 ? 'indicator' : 'indicators'
          }`,
        ]
      : []),
  ].join(' + ');

  return (
    <section className="op-data-batch-card" aria-label="Proposed operational data">
      <header className="op-data-batch-card-header">
        <div className="op-data-batch-card-title">Proposed operational data</div>
        <div className="op-data-batch-card-subtitle">
          {`${contents} · ${yearLabel}`}
          {companyName ? ` · ${companyName}` : ''}
        </div>
      </header>
      {summaryMarkdown && summaryMarkdown.length > 0 && (
        <div className="op-data-batch-card-summary">
          <ChatMarkdown>{summaryMarkdown}</ChatMarkdown>
        </div>
      )}
      <div className="op-data-batch-card-datasets">
        {datasets.map(dataset => (
          <OpDataDatasetBlock
            key={dataset.batchId}
            dataset={dataset}
            rows={workingRowsFor(dataset, working)}
            state={stateFor(dataset.batchId)}
            canApply={variantId !== null}
            onEditPercent={editPercent}
            onEditKey={editKey}
            onAddRow={addRow}
            onRemoveRow={removeRow}
            onRequestConfirm={() => {
              setState(dataset.batchId, { phase: 'confirming' });
            }}
            onCancelConfirm={() => {
              setState(dataset.batchId, { phase: 'idle' });
            }}
            onConfirm={() => {
              void applyDataset(dataset);
            }}
          />
        ))}
        {hasIndicators && <SustainabilityIndicatorsBlock block={indicatorsBlock} showYear={false} />}
      </div>
      {!allApplied &&
        // Save all replaces more scopes at once than a single dataset's Save does, so it asks for
        // the same confirmation rather than less: without this step the broader write was the one
        // that needed only a single click.
        (confirmingAll ? (
          <div className="op-data-batch-dataset-confirm" role="group" aria-label="Confirm save all">
            <div className="op-data-batch-dataset-confirm-prompt">
              {`Save ${savableCount} ${savableCount === 1 ? 'item' : 'items'} for ${currentYear}? `}
              {savableDatasetCount > 0 &&
                "Each dataset replaces the company's existing split for its metric and dimension. "}
              {indicatorsSavable && "Indicators merge into the company's existing sustainability data. "}
              {blockedHint}
            </div>
            <div className="op-data-batch-card-actions">
              <button
                type="button"
                className="op-data-batch-card-action op-data-batch-card-action--secondary"
                onClick={() => {
                  setConfirmingAll(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="op-data-batch-card-action op-data-batch-card-action--primary"
                onClick={() => {
                  setConfirmingAll(false);
                  void saveAll();
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        ) : (
          <div className="op-data-batch-card-actions">
            <button
              type="button"
              className="op-data-batch-card-action op-data-batch-card-action--primary"
              disabled={variantId === null || savableCount === 0}
              onClick={() => {
                setConfirmingAll(true);
              }}
            >
              {savableCount > 1 ? `Save all (${savableCount})` : 'Save all'}
            </button>
          </div>
        ))}
      {!allApplied && !confirmingAll && blockedHint !== null && (
        <div className="op-data-batch-card-hint" role="status">
          {blockedHint}
        </div>
      )}
      {variantId === null && (
        <div className="op-data-batch-card-hint">
          Open this chat from a company page to save the proposed operational data.
        </div>
      )}
      {datasets.length >= MAX_OPERATIONAL_DATA_BATCH_DATASETS && (
        <div className="op-data-batch-card-hint">
          Maximum of {MAX_OPERATIONAL_DATA_BATCH_DATASETS} datasets reached — ask the assistant to split additional data
          into a follow-up proposal.
        </div>
      )}
      {anyApplied && (
        // Multi-turn ergonomics (PLT-5088): once something is saved, make incremental delivery feel
        // intentional — the data is in the form (tagged as agent-sourced) and more can follow later.
        <div className="op-data-batch-card-footer">
          Saved data now appears in the company&apos;s {currentYear} operational data, tagged <em>source: agent</em>,
          and is used the next time the assessment is rerun. You can upload more data — other metrics or dimensions —
          anytime.
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-components — dataset block, validation chips, actions, receipt.
// ---------------------------------------------------------------------------

function OpDataDatasetBlock({
  dataset,
  rows,
  state,
  canApply,
  onEditPercent,
  onEditKey,
  onAddRow,
  onRemoveRow,
  onRequestConfirm,
  onCancelConfirm,
  onConfirm,
}: {
  dataset: ProposedDataset;
  rows: WorkingRow[];
  state: DatasetState;
  canApply: boolean;
  onEditPercent: (dataset: ProposedDataset, idx: number, value: string) => void;
  onEditKey: (dataset: ProposedDataset, idx: number, value: string) => void;
  onAddRow: (dataset: ProposedDataset) => void;
  onRemoveRow: (dataset: ProposedDataset, idx: number) => void;
  onRequestConfirm: () => void;
  onCancelConfirm: () => void;
  onConfirm: () => void;
}) {
  const validation = validateDataset(dataset, rows);
  const total = toEffective(rows).reduce((acc, row) => acc + (row.percentage ?? 0), 0);
  // The year lives in the card subtitle ("From the 2025 report · saved for 2026"), not per dataset.
  const scopeLabel = datasetScopeLabel(dataset);

  if (state.phase === 'applied') {
    return <OpDataDatasetReceipt scopeLabel={scopeLabel} result={state.result} />;
  }

  const locked = state.phase === 'confirming' || state.phase === 'applying';
  // Country splits are editable against the standard country list; industry splits are a fixed
  // high-impact-industry enum, so their keys cannot be added, removed, or changed here (PLT-5088).
  const keysEditable = dataset.dimension === CompanyOperationsDimension.COUNTRY;

  return (
    <section className="op-data-batch-dataset">
      <header className="op-data-batch-dataset-header">
        <div className="op-data-batch-dataset-title">{scopeLabel}</div>
        <div className="op-data-batch-dataset-total" aria-label="Defined total">
          {total.toFixed(1)}%
        </div>
      </header>
      {dataset.note && <div className="op-data-batch-dataset-note">{dataset.note}</div>}
      <ul className="op-data-batch-dataset-rows">
        {rows.map((row, idx) => (
          <li key={idx} className="op-data-batch-row">
            {keysEditable ? (
              <select
                className="op-data-batch-row-key-select"
                value={row.key}
                disabled={locked}
                aria-label={`Country for row ${idx + 1}`}
                onChange={e => {
                  onEditKey(dataset, idx, e.target.value);
                }}
              >
                <option value="">Select a country…</option>
                {/* Keep an agent-proposed value selectable even if it is not in the list, so the user
                    sees it (validation flags it); the standard list follows. */}
                {row.key !== '' && !isValidOperationalDataCountry(row.key) && (
                  <option value={row.key}>{`${row.key} (not in list)`}</option>
                )}
                {DMA_COUNTRIES.map(country => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            ) : (
              <span className="op-data-batch-row-key">{row.key}</span>
            )}
            <span className="op-data-batch-row-input-wrap">
              <input
                type="number"
                min={0}
                max={100}
                step={0.1}
                className="op-data-batch-row-input"
                value={row.percent}
                disabled={locked}
                aria-label={`Percentage for ${row.key || `row ${idx + 1}`}`}
                onChange={e => {
                  onEditPercent(dataset, idx, e.target.value);
                }}
              />
              <span className="op-data-batch-row-pct" aria-hidden="true">
                %
              </span>
            </span>
            {keysEditable && (
              <button
                type="button"
                className="op-data-batch-row-remove"
                disabled={locked}
                aria-label={`Remove ${row.key || `row ${idx + 1}`}`}
                onClick={() => {
                  onRemoveRow(dataset, idx);
                }}
              >
                ✕
              </button>
            )}
          </li>
        ))}
      </ul>
      {keysEditable && (
        <button
          type="button"
          className="op-data-batch-row-add"
          disabled={locked}
          onClick={() => {
            onAddRow(dataset);
          }}
        >
          + Add country
        </button>
      )}
      <OpDataValidationChips validation={validation} />
      <OpDataDatasetActions
        canApply={canApply}
        canSave={validation.canSave}
        state={state}
        onRequestConfirm={onRequestConfirm}
        onCancelConfirm={onCancelConfirm}
        onConfirm={onConfirm}
      />
      {state.phase === 'failed' && (
        <div className="op-data-batch-dataset-error" role="alert">
          {state.message}
        </div>
      )}
    </section>
  );
}

function OpDataValidationChips({ validation }: { validation: OperationalDataValidationResult }) {
  if (validation.errors.length === 0 && validation.warnings.length === 0) return null;
  return (
    <>
      {validation.errors.length > 0 && (
        <div className="op-data-batch-dataset-errors" role="alert">
          {validation.errors.map((msg, i) => (
            <span key={`e-${i}`}>{msg}</span>
          ))}
        </div>
      )}
      {validation.warnings.length > 0 && (
        <div className="op-data-batch-dataset-chips">
          {validation.warnings.map((msg, i) => (
            <Badge key={`w-${i}`} tone="neutral">
              {msg}
            </Badge>
          ))}
        </div>
      )}
    </>
  );
}

function OpDataDatasetActions({
  canApply,
  canSave,
  state,
  onRequestConfirm,
  onCancelConfirm,
  onConfirm,
}: {
  canApply: boolean;
  canSave: boolean;
  state: DatasetState;
  onRequestConfirm: () => void;
  onCancelConfirm: () => void;
  onConfirm: () => void;
}) {
  if (state.phase === 'confirming') {
    return (
      <div className="op-data-batch-dataset-confirm" role="group" aria-label="Confirm save">
        <div className="op-data-batch-dataset-confirm-prompt">
          Save this dataset for {new Date().getFullYear()}? It replaces the company&apos;s existing split for this
          metric and dimension.
        </div>
        {/* Secondary first, primary last — the one control order the product uses everywhere. */}
        <div className="op-data-batch-dataset-action-row">
          <button
            type="button"
            className="op-data-batch-card-action op-data-batch-card-action--secondary"
            onClick={onCancelConfirm}
          >
            Cancel
          </button>
          <button
            type="button"
            className="op-data-batch-card-action op-data-batch-card-action--primary"
            onClick={onConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    );
  }

  const isApplying = state.phase === 'applying';
  return (
    <div className="op-data-batch-dataset-action-row">
      {/* Tinted, not solid: the card's one solid control is "Save all". */}
      <button
        type="button"
        className="op-data-batch-card-action op-data-batch-card-action--secondary"
        disabled={!canApply || !canSave || isApplying}
        onClick={onRequestConfirm}
      >
        {isApplying ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

function OpDataDatasetReceipt({ scopeLabel, result }: { scopeLabel: string; result: ApplyOperationalDataBatchResult }) {
  return (
    <section className="op-data-batch-dataset op-data-batch-dataset--receipt" aria-label="Saved operational data">
      <header className="op-data-batch-dataset-header">
        <div className="op-data-batch-dataset-title">{scopeLabel}</div>
        {/* Neutral: "saved" is an operation outcome, not positive impact. */}
        <Badge tone="neutral">{result.alreadyApplied ? 'Already saved' : 'Saved'}</Badge>
      </header>
      <div className="op-data-batch-dataset-receipt-detail">
        {result.rowCount} {result.rowCount === 1 ? 'row' : 'rows'} saved for {result.replacedScope.year}
        {result.writtenKeys.length > 0 && ` · ${result.writtenKeys.join(', ')}`}
      </div>
    </section>
  );
}
